/** @jsxRuntime classic */ // <-- remove this when TS updated to 4.1
/** @jsx jsx */ // <-- remove this when TS updated to 4.1
import { jsx } from '@emotion/core';
import { Fragment, MutableRefObject, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { CreditCard } from 'phosphor-react';
import { Text } from '../..';
import { PaymentMethodsConfig } from '../../../state';
import { Copy, Discounts, ThemeProps } from '../../../types';
import BraintreeDropin from './BraintreeDropin';
import VodafonePay from './VodafonePay';
import { Vodafone } from '../../../assets';

interface PaymentMethodsProps extends Copy {
  /**
   * The css class for the input. Handled internally by emotion
   */
  className?: string;
  /**
   * Array of payment methods
   */
  paymentMethods: PaymentMethodsConfig;
  /**
   * The selected plan id
   */
  productId: number;
  /**
   * A boolean to indicate whether to show VF Pay
   */
  showVFPay: boolean;
  /**
   * Specific payment partner of the current voucher, if defined
   */
  voucherData: Discounts[];
}

const getMethodsAvailable: (
  paymentMethods: PaymentMethodsConfig,
  showVFPay: boolean,
  voucherData: Discounts[]
) => string[] = (paymentMethods, showVFPay, voucherData) => {

  const enabledMethods = new Array<string>();
  //Checks if the voucher's partner matches up with any enabled payment methods
  //and exclusively returns that if so
  //If there's no voucher partner just return braintree (AZ-2625)
  if (!!voucherData && voucherData.length > 0) {
    let foundPartner: boolean = false;

    for (let i = 0; i < voucherData.length; i++) {
      if (voucherData[i].partner === "VODAFONE" && paymentMethods.vodafonePay && showVFPay) {
        enabledMethods.push('vodafonePay');
        foundPartner = true;
      }
    }

    if (!foundPartner && paymentMethods.braintree){
      enabledMethods.push('braintree');
    }
  }
  //If no voucher data, just regularly show any available payment partners
  else {
    if (paymentMethods.vodafonePay && showVFPay) {
      enabledMethods.push('vodafonePay');
    }
    if (paymentMethods.braintree) {
      enabledMethods.push('braintree');
    }
  }

  return enabledMethods;
};


/**
 * Automatically select if only 1 payment method exists
 */
const defaultState = (payMethodAvailable: string[]) =>
  payMethodAvailable.length > 1 ? null : payMethodAvailable[0];

const PaymentMethods = ({
  className,
  fallbackCopy,
  localeCopy,
  paymentMethods,
  productId,
  showVFPay,
  voucherData
}: PaymentMethodsProps) => {
  const theme: ThemeProps = useTheme();
  const payMethodAvailable: MutableRefObject<string[]> = useRef(
    getMethodsAvailable(paymentMethods, showVFPay, voucherData)
  );
  const [selectedMethod, setSelectedMethod] = useState<string | null>(() =>
    defaultState(payMethodAvailable.current)
  );

  const onlyOnePaymentMethod: boolean = payMethodAvailable.current.length === 1;

  return (
    <div className={className}>
      {(!onlyOnePaymentMethod && <fieldset>
        {payMethodAvailable.current.map((payMethod: string, index: number) => (
          <div
            className='radio-container'
            css={{
              borderColor:
                selectedMethod === payMethod
                  ? theme.colors.accent
                  : theme.colors.common.grey,
            }}
            key={index}
            onClick={() => setSelectedMethod(payMethod)}>
            <Text
              as='label'
              color='primary'
              htmlFor={payMethod}
              variant='p'
              weight='light'>
              {payMethod === 'braintree' ? (
                <CreditCard className='svg-card' size={36} />
              ) : (
                <Vodafone className='svg-card' size={36} />
              )}
              {localeCopy?.paymentMethods[payMethod]?.title ||
                fallbackCopy.paymentMethods[payMethod].title}
            </Text>

            <input
              checked={selectedMethod === payMethod}
              id={payMethod}
              name='paymentMethod'
              onChange={() => setSelectedMethod(payMethod)}
              type='radio'
              value={payMethod}
            />
          </div>
        ))}
      </fieldset>)}
      <div className='payment-container'>
        {payMethodAvailable.current.map((payMethod: string, index: number) => (
          <Fragment key={index}>
            {payMethod === 'braintree' && selectedMethod === 'braintree' && (
              <BraintreeDropin
                fallbackCopy={fallbackCopy}
                localeCopy={localeCopy}
              />
            )}
            {payMethod === 'vodafonePay' &&
              selectedMethod === 'vodafonePay' &&
              showVFPay && (
                <VodafonePay
                  fallbackCopy={fallbackCopy}
                  localeCopy={localeCopy}
                />
              )}
          </Fragment>
        ))}
      </div>
    </div>
  );
};

const StyledPaymentMethods = styled((props: any) => (
  <PaymentMethods {...props} />
))`
  ${({
  paymentMethods,
  showVFPay,
  voucherData,
  theme,
}: {
  paymentMethods: PaymentMethodsConfig;
  showVFPay: boolean;
  voucherData: Discounts[]
  theme: ThemeProps;
}) => {
    const methodsAvail = getMethodsAvailable(paymentMethods, showVFPay, voucherData);
    return {
      width: '100%',
      '.payment-container': {
        alignItems: 'center',
        display: 'flex',
        borderColor: theme.colors.common.lightGrey,
        flexDirection: 'column',
        width: '100%',
      },
      '.radio-container': {
        alignItems: 'center',
        borderStyle: 'solid',
        borderRadius: 5,
        borderWidth: 2,
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        paddingBottom: 10,
        paddingTop: 10,
        '&:nth-of-type(1)': {
          marginRight: methodsAvail.length > 1 ? '2%' : 'initial',
        },
        '&:nth-of-type(2)': {
          marginLeft: methodsAvail.length > 1 ? '2%' : 'initial',
        },
      },
      '.svg-card': {
        marginBottom: 10,
      },
      fieldset: {
        alignItems: 'center',
        borderWidth: 0,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-around',
        margin: 0,
        padding: '0 0 20px 0',
      },
      label: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 20,
        width: '100%',
      },
    };
  }}
`;

export default StyledPaymentMethods;
